import { stringify } from 'qs';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'yoso';
import { useLogin } from '~/components/Auth';
import { ChallengeBoostBottomSheet } from '~/components/Challenge/Boost/BottomSheet/ChallengeBoostBottomSheet';
import { useEvent } from '~/components/Event/CheckIn/CheckIn.hooks';
import { CheckInResultPopup } from '~/components/Event/CheckIn/Popup/CheckInResultPopup';
import { useMember } from '~/components/Member/Member.hooks';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { useSearchParams } from '~/components/Route';
import { useTransition } from '~/components/Security';

type ModalProps = {
  onClose: () => void;
};

type UrlParameterValue = string;
export const urlModalMap: Record<UrlParameterValue, React.ComponentType<ModalProps>> = {
  checkIn: function CheckInOpener({ onClose }) {
    const { open } = useModal();
    const [proceeding, proceed] = useTransition();
    const login = useLogin({ returnTargetURI: `${location.pathname}?modal=checkIn` });

    const {
      data: { isLoggedIn },
    } = useMember();

    const { isFetched: isEventFetched, isError: isEventError, data } = useEvent('CHECK_IN');
    const { isJoinable = false } = data || {};

    useEffect(() => {
      if (!proceeding) {
        proceed(async () => {
          if (!isLoggedIn) {
            onClose();
            await login();
            return;
          }

          if (isJoinable && isEventFetched) {
            await open(<CheckInResultPopup />);
            onClose();
          }

          if (isEventFetched || isEventError) {
            onClose();
          }
        });
      }
    }, [
      isEventError,
      isEventFetched,
      isJoinable,
      isLoggedIn,
      login,
      onClose,
      open,
      proceed,
      proceeding,
    ]);

    return null;
  },
  challengeGuide: function ChallengeGuideOpener({ onClose }) {
    const { open } = useBottomSheet();

    useEffect(() => {
      (async () => {
        await open(<ChallengeBoostBottomSheet />);
        onClose();
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
  },
};
export const URL_PARAMETER_KEY = 'modal';

export const UrlModalManager = () => {
  const history = useHistory();
  const params = useSearchParams<Record<string, string>>();

  const handleClose = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [URL_PARAMETER_KEY]: omitted, ...restParams } = params;
    history.replace({ pathname: history.location.pathname, search: stringify(restParams) });
  }, [history, params]);

  const Component = urlModalMap[params[URL_PARAMETER_KEY]];
  if (params[URL_PARAMETER_KEY] && Component) {
    return <Component onClose={handleClose} />;
  }

  return null;
};
